import { Dispatch, SetStateAction, useEffect } from 'react'
import { MapRef } from 'react-map-gl/mapbox'
import { GeoJSON } from 'global'

import { FeatureCollectionPhotoPoint } from 'meta/photo/photoPoint'

import { LayerIds, SourceIds } from 'client/components/Map/ids'
import { PropsMapHook } from 'client/components/Map/types'

type Props = PropsMapHook & {
  data?: FeatureCollectionPhotoPoint
  setMapRef: Dispatch<SetStateAction<MapRef | undefined>>
}

export const useLoadPhotoPoints = (props: Props): void => {
  const { data, loaded, mapRef, setMapRef } = props

  useEffect(() => {
    if (data && loaded) {
      const mapRefCurrent = mapRef.current!

      setMapRef(mapRefCurrent)

      mapRefCurrent.loadImage('/img/star.png', (error, image) => {
        if (error) {
          throw error
        }

        mapRefCurrent.addImage('star-icon', image as HTMLImageElement, { sdf: false })
        mapRefCurrent.getMap().addSource(SourceIds.photoPoints, {
          type: 'geojson',
          data: data as unknown as GeoJSON,
          cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: 35,
        })

        // Cluster star icon
        mapRefCurrent.getMap().addLayer({
          id: LayerIds.clusterStars,
          type: 'symbol',
          source: SourceIds.photoPoints,
          filter: ['has', 'point_count'],
          layout: {
            'icon-image': 'star-icon',
            'icon-size': ['interpolate', ['linear'], ['zoom'], 1, 0.07, 8, 0.08, 12, 0.09, 16, 0.13],
            'icon-allow-overlap': true,
          },
        })

        // Cluster count on top of star
        mapRefCurrent.getMap().addLayer({
          id: LayerIds.clusterCount,
          type: 'symbol',
          source: SourceIds.photoPoints,
          filter: ['has', 'point_count'],
          layout: {
            'text-field': '{point_count_abbreviated}',
            'text-font': ['Arial Unicode MS Bold', 'DIN Offc Pro Bold'],
            'text-size': 13,
            'text-offset': [0, 0.1],
            'text-anchor': 'center',
          },
          paint: {
            'text-color': '#000000',
          },
        })

        mapRefCurrent.loadImage('/img/camera.png', (error2, image2) => {
          if (error2) {
            throw error2
          }

          mapRefCurrent.addImage('camera-icon', image2 as HTMLImageElement, { sdf: false })

          // Unclustered points with star
          mapRefCurrent.getMap().addLayer({
            id: LayerIds.unclusteredPoint,
            type: 'symbol',
            source: SourceIds.photoPoints,
            filter: ['!', ['has', 'point_count']],
            layout: {
              'icon-image': 'camera-icon',
              'icon-size': ['interpolate', ['linear'], ['zoom'], 1, 0.07, 8, 0.08, 12, 0.09, 16, 0.11],
            },
          })
        })
      })
    }
  }, [data, loaded, mapRef, setMapRef])
}
